import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Leaf, Sprout, Sparkles, ArrowRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { decodeInviteFragment, resolveShortInviteSlug } from '../utils/inviteLink';
import { renderSenderNoteMarkup } from '../utils/senderNoteMarkup';

/**
 * Read-only "Grow along" landing page. A friend of an existing user opens
 * either:
 *   - `/g/<slug>`         → short-link, fetched from backend, or
 *   - `/g#v{1|2}.<data>`  → self-contained fragment (never touches server)
 * and sees the sender's plants in a clean gallery with a strong CTA to
 * spin up their own library.
 *
 * When the sender has more plants than fit in the invite (12-plant cap),
 * we surface a small "Showing N of M plants" banner so the recipient
 * knows this is a preview slice.
 */
const CARE_LABELS = {
  family: 'Family',
  plant_type: 'Type',
  watering: 'Watering',
  light: 'Light',
  humidity: 'Humidity',
  temperature: 'Temperature',
  toxicity: 'Toxicity',
  environment: 'Environment',
  growth_rate: 'Growth rate',
};

// Extended labels rendered ONLY when the invite is a single-plant "full
// detail" share (payload.kind === 'plant'). Order matters — this is the
// on-screen render order.
const CARE_LABELS_FULL = {
  ...CARE_LABELS,
  bloom_season: 'Bloom season',
  bloom_duration: 'Bloom duration',
  bloom_tips: 'Bloom tips',
  blooms_indoors: 'Blooms indoors',
  dormancy_notes: 'Dormancy notes',
  dormancy_months: 'Dormancy months',
  toxicity_notes: 'Toxicity notes',
};

const GrowAlongPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [payload, setPayload] = useState(null);
  const [decoded, setDecoded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Two source paths: path-based slug OR hash-fragment.
      if (params?.slug) {
        const p = await resolveShortInviteSlug(params.slug);
        if (!cancelled) {
          setPayload(p);
          setDecoded(true);
        }
        return;
      }
      // Fragment lives in `window.location.hash` — browsers strip it
      // before sending the HTTP request, so it never touches our server.
      if (!cancelled) {
        setPayload(decodeInviteFragment(window.location.hash));
        setDecoded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [params?.slug]);

  const plantCount = payload?.p?.length || 0;
  const isSinglePlant = payload?.kind === 'plant' && plantCount === 1;
  // Payload `tc` (total count in sender's library) + `tr` (truncated flag)
  // were added in iter 75 so recipients see "sharing 12 of 47".
  // Older payloads without these fields fall back to plantCount.
  const totalCount = typeof payload?.tc === 'number' ? payload.tc : plantCount;
  const isPartial = !isSinglePlant && !!payload?.tr && totalCount > plantCount;
  const senderLabel = useMemo(() => {
    const raw = payload?.s?.trim();
    if (!raw) return null;
    // Show only first name for privacy, in case the sender pasted their full name.
    return raw.split(/\s+/)[0];
  }, [payload]);
  // Optional sender welcome note (payload.n). Trimmed defensively and
  // capped at 240 chars matching what the composer allowed — long lists
  // are shown in a soft-tan card above the plant grid.
  const senderNote = useMemo(() => {
    const raw = typeof payload?.n === 'string' ? payload.n.trim() : '';
    if (!raw) return null;
    return raw.slice(0, 240);
  }, [payload]);

  // Deep-link view state (payload.vs) — echoes the sender's library
  // sort order so recipients open the same view. Both name sorts AND
  // group-by fields are honoured now (iter 94): when `sb` is a group
  // field (family, watering, light, humidity, temperature, toxicity,
  // environment, growth_rate, plant_type) each plant is bucketed by
  // that field and the recipient sees the same group headers the
  // sender was browsing.
  const GROUP_FIELDS = ['family', 'plant_type', 'watering', 'light', 'humidity', 'temperature', 'toxicity', 'environment', 'growth_rate'];
  const sortedPlants = useMemo(() => {
    const items = Array.isArray(payload?.p) ? [...payload.p] : [];
    const sb = payload?.vs?.sb;
    if (!sb || items.length < 2) return items;
    const dir = sb.endsWith('_desc') ? -1 : 1;
    const key = sb.replace(/_(asc|desc)$/, '');
    if (key !== 'name') return items;
    items.sort((a, b) => {
      const an = String(a?.n || '').toLowerCase();
      const bn = String(b?.n || '').toLowerCase();
      if (an === bn) return 0;
      return an < bn ? -1 * dir : 1 * dir;
    });
    return items;
  }, [payload]);

  // Group buckets — populated when `sb` is a group field. Each group is
  // sorted alphabetically by label; plants inside each group are sorted
  // by common name for a stable reading order. `secondaryLabel` shows
  // the sender's chosen "Then by…" if a group value was propagated too.
  const groupedPlants = useMemo(() => {
    const items = Array.isArray(payload?.p) ? [...payload.p] : [];
    const sb = payload?.vs?.sb;
    if (!sb || !GROUP_FIELDS.includes(sb) || items.length < 2) return null;
    const buckets = new Map();
    for (const p of items) {
      const raw = p?.c?.[sb];
      const label = (typeof raw === 'string' && raw.trim()) ? raw.trim() : 'Not specified';
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label).push(p);
    }
    // Sort plants inside each bucket by common name for a stable read.
    for (const arr of buckets.values()) {
      arr.sort((a, b) => String(a?.n || '').localeCompare(String(b?.n || ''), undefined, { sensitivity: 'base' }));
    }
    // Alphabetical group order; "Not specified" sinks to the bottom.
    return [...buckets.entries()]
      .sort((a, b) => {
        if (a[0] === 'Not specified') return 1;
        if (b[0] === 'Not specified') return -1;
        return a[0].localeCompare(b[0], undefined, { sensitivity: 'base' });
      })
      .map(([label, plants]) => ({ label, plants }));
  }, [payload]);

  // Human-readable chip shown next to the header so the recipient sees
  // why the ordering matches the sender's view. Handles both name sorts
  // and group sorts (iter 94).
  const sortChipLabel = useMemo(() => {
    const sb = payload?.vs?.sb;
    if (!sb) return null;
    if (sb === 'name_asc') return 'A → Z';
    if (sb === 'name_desc') return 'Z → A';
    if (GROUP_FIELDS.includes(sb)) return `by ${CARE_LABELS[sb] || sb}`;
    return null;
  }, [payload]);

  if (!decoded) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
        <p style={{ color: 'hsl(143, 28%, 25%)' }}>Loading…</p>
      </main>
    );
  }

  if (!payload || plantCount === 0) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-4 text-center" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
        <Leaf className="w-14 h-14 mb-4" style={{ color: 'hsl(143, 28%, 25%)' }} />
        <h1 className="font-display text-3xl font-bold mb-3" style={{ color: 'hsl(143, 28%, 25%)' }}>
          This invite link looks empty
        </h1>
        <p className="text-base mb-6 max-w-md" style={{ color: 'hsl(125, 12%, 25%)' }}>
          The plant list wasn&apos;t included in this link, or it may have expired. Ask your friend to share a fresh invite from their library.
        </p>
        <Button
          onClick={() => navigate('/')}
          style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
          data-testid="grow-along-empty-cta"
        >
          <Sprout className="w-4 h-4 mr-2" />
          Start your own plant library
        </Button>
      </main>
    );
  }

  return (
    <main className="min-h-screen" style={{ backgroundColor: 'hsl(82, 55%, 95%)' }}>
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="grow-along-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-4">
            <img src="/monstera-login-v3.png" alt="Monstera leaf" width="64" height="64" style={{ objectFit: 'contain' }} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2" style={{ color: 'hsl(143, 28%, 25%)' }}>
            {isSinglePlant
              ? (senderLabel ? `${senderLabel} shared a plant` : 'A shared plant')
              : (senderLabel ? `${senderLabel}'s plant collection` : 'A plant collection')}
          </h1>
          <p className="text-base" style={{ color: 'hsl(125, 12%, 25%)' }}>
            {isSinglePlant ? (
              <>Full care details, shared for you to grow along{senderLabel ? ` with ${senderLabel}` : ''}</>
            ) : isPartial ? (
              <>Showing <strong>{plantCount}</strong> of <strong>{totalCount}</strong> plants · shared for you to grow along{senderLabel ? ` with ${senderLabel}` : ''}</>
            ) : (
              <>{plantCount} plant{plantCount !== 1 ? 's' : ''} · shared for you to grow along{senderLabel ? ` with ${senderLabel}` : ''}</>
            )}
          </p>
        </div>

        {/* Partial-list banner — appears when the sender has more plants
            than fit in a single invite (12-plant cap). */}
        {isPartial && (
          <div
            className="flex items-start gap-3 rounded-xl px-4 py-3 mb-6 text-sm"
            style={{
              backgroundColor: 'hsl(45, 90%, 94%)',
              color: 'hsl(30, 40%, 22%)',
              border: '1px solid hsl(45, 60%, 80%)',
            }}
            role="status"
            data-testid="grow-along-partial-banner"
          >
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
            <span>
              This is a partial preview — {senderLabel ? `${senderLabel}'s` : 'the sender\u2019s'} library has {totalCount} plants total.
              Ask them to share the rest, or start your own library to build a full collection.
            </span>
          </div>
        )}

        {/* Sender welcome note — optional short greeting the sharer typed
            in the ShareChoiceModal. Rendered in a soft-tan card so it
            feels conversational and distinct from the read-only plant
            cards below. `whitespace-pre-line` preserves any line breaks
            the sender added. */}
        {senderNote && (
          <blockquote
            className="rounded-xl px-4 py-3 mb-6 text-sm whitespace-pre-line"
            style={{
              backgroundColor: 'hsl(45, 60%, 94%)',
              color: 'hsl(30, 30%, 20%)',
              borderLeft: '3px solid hsl(143, 28%, 45%)',
            }}
            data-testid="grow-along-sender-note"
          >
            <span
              className="block text-[11px] uppercase tracking-wider mb-1 font-medium"
              style={{ color: 'hsl(143, 28%, 30%)' }}
            >
              {senderLabel ? `${senderLabel} says` : 'A note from the sender'}
            </span>
            {renderSenderNoteMarkup(senderNote)}
          </blockquote>
        )}

        {/* Deep-link sort chip — surfaces the sender's chosen ordering
            so the recipient knows the cards below aren't in random
            order. Hidden for single-plant invites and when no sort was
            propagated. */}
        {sortChipLabel && !isSinglePlant && (
          <div
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs mb-4"
            style={{
              backgroundColor: 'hsl(143, 30%, 92%)',
              color: 'hsl(143, 28%, 25%)',
              border: '1px solid hsl(143, 28%, 78%)',
            }}
            data-testid="grow-along-sort-chip"
          >
            <span>{groupedPlants ? `Grouped ${sortChipLabel}` : `Sorted ${sortChipLabel}`}</span>
            {senderLabel && (
              <span style={{ color: 'hsl(125, 12%, 45%)' }}>· {senderLabel}&rsquo;s view</span>
            )}
          </div>
        )}

        {/* Plants — read-only cards. For single-plant "full detail" invites
            (payload.kind === 'plant'), use a wider single-column layout so
            all bloom/dormancy/toxicity rows have room to breathe. When
            the sender's deep-linked sort is a group field, plants are
            organised under group headers matching the sender's view. */}
        {(() => {
          const renderPlantArticle = (plant, idx) => {
            const care = plant.c || {};
            const labels = isSinglePlant ? CARE_LABELS_FULL : CARE_LABELS;
            const rows = Object.keys(labels)
              .filter((k) => care[k])
              .map((k) => ({ label: labels[k], value: care[k] }));
            const thumb = plant.t;
            const thumbSize = isSinglePlant ? 128 : 72;
            return (
              <article
                key={idx}
                className={
                  isSinglePlant
                    ? 'rounded-2xl p-6 shadow-sm bg-white flex flex-col sm:flex-row gap-5'
                    : 'rounded-2xl p-4 shadow-sm bg-white flex gap-3'
                }
                data-testid={`grow-along-plant-${idx}`}
              >
                <div
                  className="flex-shrink-0 rounded-xl overflow-hidden"
                  style={{
                    width: thumbSize,
                    height: thumbSize,
                    backgroundColor: 'hsl(143, 28%, 92%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-hidden="true"
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      width={thumbSize}
                      height={thumbSize}
                      style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                      data-testid={`grow-along-plant-${idx}-thumb`}
                    />
                  ) : (
                    <Leaf className={isSinglePlant ? 'w-12 h-12' : 'w-7 h-7'} style={{ color: 'hsl(143, 30%, 55%)' }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2
                    className={isSinglePlant ? 'text-2xl font-bold leading-tight' : 'text-lg font-bold leading-tight'}
                    style={{ color: 'hsl(143, 28%, 25%)' }}
                  >
                    {plant.n}
                  </h2>
                  {plant.s && (
                    <p
                      className={isSinglePlant ? 'text-base italic mb-3' : 'text-sm italic mb-2'}
                      style={{ color: 'hsl(125, 12%, 45%)' }}
                    >
                      {plant.s}
                    </p>
                  )}
                  {rows.length > 0 && (
                    <dl className="text-sm space-y-1 mt-2">
                      {rows.map((r) => (
                        <div key={r.label} className="flex gap-2">
                          <dt
                            className="font-semibold"
                            style={{ color: 'hsl(143, 20%, 35%)', minWidth: isSinglePlant ? 130 : 80 }}
                          >
                            {r.label}
                          </dt>
                          <dd style={{ color: 'hsl(125, 12%, 25%)' }}>{r.value}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </article>
            );
          };

          if (groupedPlants && !isSinglePlant) {
            let running = 0;
            return (
              <div className="mb-10" data-testid="grow-along-grouped-view">
                {groupedPlants.map((group) => (
                  <section key={group.label} className="mb-8" data-testid={`grow-along-group-${group.label}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <h2
                        className="text-base font-semibold px-3 py-1 rounded-full"
                        style={{
                          backgroundColor: group.label === 'Not specified' ? 'hsl(0, 0%, 92%)' : 'hsl(143, 28%, 90%)',
                          color: group.label === 'Not specified' ? 'hsl(0, 0%, 45%)' : 'hsl(143, 28%, 25%)',
                        }}
                      >
                        {group.label}
                      </h2>
                      <span className="text-xs" style={{ color: 'hsl(125, 12%, 42%)' }}>
                        {group.plants.length} {group.plants.length !== 1 ? 'plants' : 'plant'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {group.plants.map((plant) => {
                        const idx = running++;
                        return renderPlantArticle(plant, idx);
                      })}
                    </div>
                  </section>
                ))}
              </div>
            );
          }

          return (
            <div
              className={isSinglePlant ? 'mb-10' : 'grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10'}
            >
              {sortedPlants.map((plant, idx) => renderPlantArticle(plant, idx))}
            </div>
          );
        })()}

        {/* Sticky CTA card */}
        <section
          className="rounded-2xl p-6 sm:p-8 text-center shadow-md"
          style={{ background: 'linear-gradient(135deg, hsl(143, 28%, 25%) 0%, hsl(143, 40%, 32%) 100%)', color: 'white' }}
          data-testid="grow-along-cta-card"
        >
          <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-90" />
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">
            Start your own plant library
          </h2>
          <p className="text-sm sm:text-base opacity-90 mb-5 max-w-md mx-auto">
            Track your houseplants, identify new ones with AI, and never forget a watering schedule. Free · no account required · works offline.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="text-base font-semibold"
            style={{ backgroundColor: 'white', color: 'hsl(143, 28%, 25%)' }}
            data-testid="grow-along-primary-cta"
          >
            <Sprout className="w-5 h-5 mr-2" />
            Start my library
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>

        {/* Footer — attribution */}
        <p className="text-center text-xs mt-6" style={{ color: 'hsl(125, 12%, 45%)' }}>
          Shared via personalplants.online · read-only
        </p>
      </div>
    </main>
  );
};

export default GrowAlongPage;
